import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Send, ArrowLeft, AlertTriangle } from "lucide-react";

export default function IndentForm() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isSiteEngineer = user?.role === "site_engineer";
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [boq, setBoq] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("");
  const [urgency, setUrgency] = useState("Normal");
  const [remarks, setRemarks] = useState("");
  const [rateOverride, setRateOverride] = useState("");
  const [overrideRemark, setOverrideRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data || [])); }, []);
  useEffect(() => {
    if (!projectId) { setBoq(null); return; }
    api.get(`/projects/${projectId}/boq`).then((r) => setBoq(r.data)).catch(() => setBoq(null));
  }, [projectId]);

  const filteredLines = useMemo(() => {
    const lines = (boq?.status === "Approved" ? boq.lines : []) || [];
    const s = q.trim().toLowerCase();
    if (!s) return lines.slice(0, 200);
    return lines.filter((l) => l.iow_code.toLowerCase().includes(s) || (l.description || "").toLowerCase().includes(s)).slice(0, 200);
  }, [boq, q]);

  const rate = selected ? (rateOverride !== "" ? parseFloat(rateOverride) : selected.rate) : 0;
  const value = (parseFloat(qty) || 0) * (rate || 0);

  const [budgetCheck, setBudgetCheck] = useState(null);
  const [usedQty, setUsedQty] = useState(0);
  useEffect(() => {
    if (!selected || !projectId) { setBudgetCheck(null); setUsedQty(0); return; }
    api.get(`/indents`, { params: { project_id: projectId } }).then(({ data }) => {
      const prev = data.filter((x) => x.iow_code === selected.iow_code && ["Approved", "Auto-Approved", "Pending"].includes(x.status));
      const used = prev.reduce((a, x) => a + x.qty, 0);
      setUsedQty(used);
      if (qty) {
        const cum = used + parseFloat(qty || 0);
        const pct = selected.qty > 0 ? (cum / selected.qty) * 100 : 0;
        setBudgetCheck({ cum, pct, flag: pct >= 90 });
      } else {
        setBudgetCheck(null);
      }
    }).catch(() => { setBudgetCheck(null); setUsedQty(0); });
  }, [selected, projectId, qty]);
  const availableQty = selected ? Math.max(0, (selected.qty || 0) - usedQty) : 0;

  const submit = async () => {
    if (!selected || !projectId || !qty) return;
    setSubmitting(true); setErr("");
    try {
      const body = {
        project_id: projectId,
        iow_code: selected.iow_code,
        qty: parseFloat(qty),
        urgency,
        remarks,
      };
      if (!isSiteEngineer && rateOverride !== "") {
        body.rate_override = parseFloat(rateOverride);
        body.rate_override_remark = overrideRemark;
      }
      await api.post("/indents", body);
      nav("/indents");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0D3B66]"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">New Indent</h1>
        <p className="mt-1.5 text-slate-500">Raise an indent against an approved BOQ line.</p>
      </div>

      <Card className="p-5 space-y-5 border-slate-200">
        <div className="space-y-1.5">
          <Label>Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="indent-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {projectId && boq && boq.status !== "Approved" && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            The BOQ for this project is not yet approved. You cannot raise indents until approval.
          </div>
        )}

        <div className="space-y-1.5">
          <Label>IOW Code</Label>
          <button
            type="button"
            data-testid="indent-iow-picker-btn"
            onClick={() => setPickerOpen(true)}
            disabled={!projectId || boq?.status !== "Approved"}
            className="w-full h-11 rounded-md border border-slate-300 px-3 flex items-center justify-between text-left hover:border-[#F97316] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="truncate">
              {selected ? (
                <span className="text-sm">
                  <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5 mr-2">{selected.iow_code}</span>
                  <span className="text-slate-800">{selected.description}</span>
                </span>
              ) : (
                <span className="text-slate-400 text-sm">Search and pick an item…</span>
              )}
            </span>
            <Search className="h-4 w-4 text-slate-400" />
          </button>
        </div>

        {selected && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>UOM</Label>
                <Input value={selected.uom} disabled className="h-11" data-testid="indent-uom" />
              </div>
              <div className="space-y-1.5">
                <Label>Qty</Label>
                <Input data-testid="indent-qty-input" type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>Urgency</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger data-testid="indent-urgency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Available Budget Quantity</Label>
              <div data-testid="indent-available-qty" className="h-11 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center justify-between">
                <span className="text-lg font-semibold text-[#0D3B66]">{availableQty.toLocaleString("en-IN", { maximumFractionDigits: 4 })} {selected.uom}</span>
                <span className="text-[11px] text-slate-500">Budget {selected.qty} · Used {usedQty.toLocaleString("en-IN", { maximumFractionDigits: 4 })}</span>
              </div>
            </div>

            {!isSiteEngineer && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Rate (BOQ: {fmtINR(selected.rate)})</Label>
                  <Input data-testid="indent-rate-input" type="number" placeholder={String(selected.rate)} value={rateOverride} onChange={(e) => setRateOverride(e.target.value)} className="h-11" />
                  <p className="text-[11px] text-slate-500">Leave blank to use BOQ rate. Override requires a remark.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Estimated Value</Label>
                  <div data-testid="indent-value" className="h-11 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-lg font-semibold text-[#0D3B66]">
                    {fmtINR(value)}
                  </div>
                </div>
              </div>
            )}

            {!isSiteEngineer && rateOverride !== "" && (
              <div className="space-y-1.5">
                <Label>Rate override remark (required)</Label>
                <Input data-testid="indent-override-remark" value={overrideRemark} onChange={(e) => setOverrideRemark(e.target.value)} placeholder="Reason for rate change" className="h-11" />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Remarks</Label>
              <Textarea data-testid="indent-remarks-input" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional notes for the approver…" />
            </div>

            {budgetCheck?.flag && (
              <div data-testid="budget-warning" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <b>Budget alert:</b> This indent brings cumulative qty to {budgetCheck.pct.toFixed(1)}% of the budgeted qty for this IOW. You may still submit; the approver will see this flag.
                </div>
              </div>
            )}

            {err && <div data-testid="indent-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{err}</div>}

            <Button
              data-testid="indent-submit-btn"
              onClick={submit}
              disabled={submitting || !qty || (!isSiteEngineer && rateOverride !== "" && !overrideRemark.trim())}
              className="w-full h-11 rounded-full bg-[#0D3B66] hover:bg-[#114B82]"
            >
              <Send className="h-4 w-4 mr-1.5" /> {submitting ? "Submitting…" : "Submit indent"}
            </Button>
          </>
        )}
      </Card>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl p-0" data-testid="iow-picker">
          <div className="p-4 border-b border-slate-100">
            <DialogHeader><DialogTitle>Pick IOW code</DialogTitle></DialogHeader>
          </div>
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="iow-picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search IOW or description…" className="pl-9 h-10" />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-auto">
            {filteredLines.map((l) => (
              <button
                key={l.iow_code}
                data-testid={`iow-pick-${l.iow_code}`}
                onClick={() => { setSelected(l); setPickerOpen(false); }}
                className="w-full text-left px-4 py-2 border-b border-slate-100 hover:bg-slate-50 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{l.description}</div>
                  <div className="text-xs text-slate-500">{l.iow_code} · {l.uom} · budget qty {l.qty}</div>
                </div>
                {!isSiteEngineer && (
                  <div data-testid={`iow-pick-rate-${l.iow_code}`} className="text-sm font-semibold text-[#0D3B66]">{fmtINR(l.rate)}</div>
                )}
              </button>
            ))}
            {filteredLines.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No matches</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
